import { CustomFieldTypes } from '@generated/graphql';
import type { CustomFieldValue } from '@interfaces/customField';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { UserContext } from '@providers/userContextProvider';
import dayjs from 'dayjs';
import { Box, Typography } from 'hephaestus/web';
import { groupBy } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../hooks/useLocalization';

export const CustomFieldValueDisplay = ({
  field,
}: {
  field: CustomFieldValue;
}) => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const dateFormat =
    activeOrganization.applicableLocalization?.dateFormat ?? 'YYYY-MM-DD';
  const { t } = useTranslation(['workflowCompletions']);

  if (field.customField.type === CustomFieldTypes.Boolean) {
    const typedField = field as CustomFieldValue<CustomFieldTypes.Boolean>;

    if (typedField.value === undefined) return null;
    return (
      <Badge
        color={typedField.value ? 'positive' : 'negative'}
        label={t(`customFieldValues.boolean.${field.value}`)}
      />
    );
  }

  if (field.customField.type === CustomFieldTypes.SingleSelect) {
    const typedField = field as CustomFieldValue<CustomFieldTypes.SingleSelect>;
    const optionDisplays = groupBy(
      typedField.customField?.selectOptions,
      (o) => o.value,
    );

    if (typedField.value === undefined) return null;
    return (
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {translateBackendString(
          optionDisplays?.[typedField.value]?.at(0)?.displayText,
        )}
      </Typography>
    );
  }

  if (field.customField.type === CustomFieldTypes.MultipleSelect) {
    const typedField =
      field as CustomFieldValue<CustomFieldTypes.MultipleSelect>;
    const optionDisplays = groupBy(
      typedField.customField?.selectOptions,
      (o) => o.value,
    );

    if (typedField.value === undefined) return null;
    return (
      <Box
        sx={{
          gap: 1,
          display: 'flex',
        }}
      >
        {typedField.value.map((v) => (
          <Badge
            key={v}
            label={translateBackendString(
              optionDisplays[v]?.at(0)?.displayText,
            )}
          />
        ))}
      </Box>
    );
  }

  if (field.customField.type === CustomFieldTypes.Date) {
    const typedField = field as CustomFieldValue<CustomFieldTypes.Date>;

    if (typedField.value === undefined) return null;
    return (
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {dayjs(typedField.value).format(dateFormat)}
      </Typography>
    );
  }

  if (field.customField.type === CustomFieldTypes.CustomObject) {
    /** @todo: implement this as part of https://onewealth.atlassian.net/browse/OV-19381 */
    return null;
  }

  return (
    <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
      {field.value}
    </Typography>
  );
};
